import { type FC, memo, useCallback, useEffect, useState } from 'react';
import * as THREE from 'three';
import type { Number3, ViewerObjectProps } from '../types';
import { type BinPoints } from './Loaders/BinPointsLoader';
import { PointCloudMaterial } from './PointCloudMaterial';
import { ViewerLayer } from './ViewerLayer';
import { RENDER_ORDER_POINT_CLOUD } from './RenderOrder';
import { useAbyssViewerContext } from './AbyssViewerContext';

export interface PointCloudNodeFromDataProps extends ViewerObjectProps {
  binPoints: BinPoints;
  octreeId: string;
  url: string;
  material: string;
  showInSphericalView?: boolean;
  position?: Parameters<THREE.Vector3['set']>;
  rotation?: Parameters<THREE.Vector3['set']>;
  getPointCloudMaterial: (material: string) => PointCloudMaterial | undefined;
}

export const PointCloudNodeFromData: FC<PointCloudNodeFromDataProps> = memo(
  ({
    binPoints,
    octreeId,
    url,
    material,
    showInSphericalView,
    position,
    rotation,
    isVisible,
    getPointCloudMaterial,
  }) => {
    const { pickHelper } = useAbyssViewerContext();
    const { positionData, attributes }: BinPoints = binPoints;

    const [points, setPoints] = useState<THREE.Points>();
    const [bufferGeometry, setBufferGeometry] = useState<THREE.BufferGeometry>();

    const pointsCallback = useCallback(
      (points: THREE.Points) => {
        if (points !== null) {
          setPoints(points);
          const { layers } = points;
          if (
            showInSphericalView !== undefined &&
            showInSphericalView &&
            (isVisible === undefined || isVisible)
          ) {
            layers.enable(ViewerLayer.SphericalViewerLayer);
          } else {
            layers.disable(ViewerLayer.SphericalViewerLayer);
          }
          if (isVisible === undefined || isVisible) {
            layers.enable(ViewerLayer.Default);
            layers.enable(ViewerLayer.EDLShaderLayer);
          } else {
            layers.disable(ViewerLayer.Default);
            layers.disable(ViewerLayer.EDLShaderLayer);
          }
          layers.disable(ViewerLayer.AutoPickable);
        }
      },
      [showInSphericalView, isVisible]
    );

    const getPointAttribute = useCallback(
      (pointIndex: number, attributeName: string): number | Number3 => {
        if (attributeName === 'position') {
          const positionVec = new THREE.Vector3(
            positionData.getX(pointIndex),
            positionData.getY(pointIndex),
            positionData.getZ(pointIndex)
          );
          if (points) {
            positionVec.applyMatrix4(points.matrixWorld);
          }

          return positionVec.toArray();
        }
        const attributeInfo = attributes.find((attribute) => attribute.name === attributeName);
        if (attributeInfo === undefined) {
          throw new Error(`Attribute not found: ${attributeName}`);
        }
        if (attributeName === 'color') {
          return [
            attributeInfo.data.getX(pointIndex),
            attributeInfo.data.getY(pointIndex),
            attributeInfo.data.getZ(pointIndex),
          ];
        }
        // an annotation id
        if (attributeInfo.attributeBuffer === undefined) {
          throw new Error(`Attribute has missing attributeBuffer: ${attributeName}`);
        }
        const value = attributeInfo.attributeBuffer.getValue(pointIndex);
        return value;
      },
      [positionData, attributes, points]
    );

    const getPointAttributes = useCallback(
      (pointIndex: number, attributeNames: string[]) =>
        new Map<string, number | number[]>(
          attributeNames.map((name) => [name, getPointAttribute(pointIndex, name)])
        ),
      [getPointAttribute]
    );

    const pointCloudMaterial = getPointCloudMaterial(material);

    const geometryCallback = useCallback((bufferGeometry: THREE.BufferGeometry) => {
      if (bufferGeometry) {
        setBufferGeometry(bufferGeometry);
      }
    }, []);

    useEffect(() => {
      if (!bufferGeometry) return;
      if (!points) return;
      if (!pickHelper) return;
      if (!pointCloudMaterial) return;

      bufferGeometry.setAttribute('position', positionData);
      const updatedShaderAttributeNames = new Set<string>();

      const addGeometryAttribute = (
        attributeName?: string,
        shaderAttributeName?: string,
        shouldCheck?: boolean
      ) => {
        if (attributeName === undefined) {
          return;
        }
        const materialAttribute = attributes.find((attribute) => attribute.name === attributeName);
        if (materialAttribute && shaderAttributeName) {
          if (shouldCheck ?? false) {
            pointCloudMaterial.assertCompatible(attributeName, materialAttribute.attributeBuffer);
          }
          if (!updatedShaderAttributeNames.has(shaderAttributeName)) {
            bufferGeometry.setAttribute(shaderAttributeName, materialAttribute.data);
            updatedShaderAttributeNames.add(shaderAttributeName);
          }
        } else {
          throw new Error(
            `Material attribute '${attributeName}' not found in point cloud data format`
          );
        }
      };

      const {
        attributeName,
        shaderAttributeName,
        visibilityAttributeName,
        shaderVisibilityAttributeName,
        hasColor,
      } = pointCloudMaterial;

      const shouldCheckCompatibility = true;
      addGeometryAttribute(attributeName, shaderAttributeName, shouldCheckCompatibility);
      addGeometryAttribute(
        visibilityAttributeName,
        shaderVisibilityAttributeName,
        shouldCheckCompatibility
      );

      if (hasColor) {
        addGeometryAttribute('color', 'color');
      }

      pickHelper.removePointCloudNode(url);
      pickHelper.addPointCloudNode(
        octreeId,
        url,
        points,
        bufferGeometry,
        pointCloudMaterial,
        getPointAttribute,
        getPointAttributes
      );
    }, [
      url,
      octreeId,
      points,
      bufferGeometry,
      pointCloudMaterial,
      positionData,
      attributes,
      getPointAttribute,
      getPointAttributes,
      pickHelper,
    ]);

    useEffect(() => {
      return () => {
        pickHelper?.removePointCloudNode(url);
      };
    }, [url, pickHelper]);

    return (
      pointCloudMaterial && (
        <points
          ref={pointsCallback}
          position={position}
          rotation={rotation}
          material={pointCloudMaterial.shaderMaterial}
          key={url}
          renderOrder={RENDER_ORDER_POINT_CLOUD}
        >
          <bufferGeometry ref={geometryCallback} attach="geometry" />
        </points>
      )
    );
  }
);
PointCloudNodeFromData.displayName = 'PointCloudNodeFromData';
